import { useEffect, useMemo, useState, useContext } from 'react';
import {
  Check,
  ChevronRight,
  Coins,
  Gift,
  HandPlatter,
  Menu as MenuIcon,
  Search,
  ShoppingBag,
  Tag,
  User as UserIcon,
  Users,
  X,
} from 'lucide-react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import ItemCustomization from '../../components/productDetails/ItemCustomization';
import CartContext from '../../Context/Cart/CartContext';
import { GetActiveCampaigns, GetMenuItemsByMerchant, GetMerchantProfile, GetRewards } from '../../hooks/reactQuery';
import { useCustomerLookup } from '../../hooks/reactQuery/useCustomer';
import { useMerchantSettings } from '../../hooks/reactQuery/useMerchant';
import { formatCurrency } from '../../Utils';
import { useBrand } from '../../Context/Brand/BrandContext';
import TableSessionContext from '../../Context/TableSession/TableSessionContext';
import SharedTablePanel, { JoinTableSheet } from '../../components/SharedTable/SharedTablePanel';

const FALLBACK_MERCHANT = { name: 'Restaurant', description: '' };
const normalizeText = (value) => (value || '').toString().trim();
const itemKey = (item) => item.item_public_id || item.item_id || item.id || item.name;

function TopSellerCard({ item, onSelect }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(item)}
      className="relative h-[132px] min-w-[178px] max-w-[178px] snap-start overflow-hidden rounded-xl bg-neutral-900 text-left sm:min-w-[210px] sm:max-w-[210px]"
    >
      {item.image ? (
        <img src={item.image} alt={item.name} className="absolute inset-0 h-full w-full object-cover" />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center bg-neutral-100">
          <HandPlatter className="h-8 w-8 text-neutral-300" />
        </div>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 p-3.5 text-white">
        <p className="line-clamp-2 text-[15px] font-semibold leading-5">{item.name}</p>
        <p className="mt-1 text-[13px] font-bold">{formatCurrency(item.price)}</p>
      </div>
    </button>
  );
}

function MenuItemRow({ item, accentColor, onSelect }) {
  return (
    <article className="rounded-xl border border-neutral-200 bg-white px-3 py-3.5">
      <button type="button" onClick={() => onSelect(item)} className="block w-full text-left">
        <div className={item.image ? 'grid grid-cols-[minmax(0,1fr)_84px] items-start gap-4' : ''}>
          <h3 className="text-[16px] font-semibold leading-5 text-neutral-950">{item.name}</h3>
          {item.image && (
            <span className="h-[76px] w-[84px] overflow-hidden rounded-lg bg-neutral-100">
              <img src={item.image} alt={item.name} className="h-full w-full object-cover" />
            </span>
          )}
        </div>
        {item.description && (
          <p className="mt-2 text-[14px] leading-5 text-neutral-600">{item.description}</p>
        )}
      </button>
      <div className="mt-3 flex items-center justify-between gap-3">
        <p className="text-[14px] font-bold text-neutral-950">{formatCurrency(item.price)}</p>
        <button
          type="button"
          onClick={() => onSelect(item)}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-white active:scale-95"
          style={{ backgroundColor: accentColor }}
          aria-label={`Add ${item.name}`}
        >
          <span className="text-[20px] leading-none">+</span>
        </button>
      </div>
    </article>
  );
}

function OfferStrip({ activeCampaigns, qualifyingRewards, accentColor }) {
  const entries = [
    ...activeCampaigns.map((campaign) => ({
      id: `campaign-${campaign.id}`,
      icon: Tag,
      type: 'Offer',
      title: campaign.name,
      note: campaign.description || 'Applied at checkout',
    })),
    ...qualifyingRewards.map((reward) => ({
      id: `reward-${reward.id}`,
      icon: Gift,
      type: 'Reward',
      title: reward.name,
      note: `${Number(reward.points_required || 0).toLocaleString()} points`,
    })),
  ];

  if (!entries.length) return null;

  return (
    <section className="mt-5">
      <div className="mb-2 flex items-center justify-between gap-3">
        <h2 className="text-[14px] font-bold text-neutral-950">Available today</h2>
        {entries.length > 1 && <span className="text-[11px] text-neutral-500">{entries.length} offers</span>}
      </div>
      <div className="flex snap-x gap-2.5 overflow-x-auto pb-1 scrollbar-none">
        {entries.map((entry) => {
          const Icon = entry.icon;
          return (
            <div key={entry.id} className="flex min-w-[calc(100%_-_28px)] snap-start items-center gap-3 rounded-xl border border-neutral-200 bg-[#fafaf8] px-3 py-3 sm:min-w-[280px]">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg" style={{ backgroundColor: `${accentColor}12`, color: accentColor }}>
                <Icon className="h-4 w-4" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-[10px] font-bold uppercase text-neutral-500">{entry.type}</p>
                <p className="mt-0.5 line-clamp-1 text-[13px] font-semibold text-neutral-950">{entry.title}</p>
                <p className="mt-0.5 line-clamp-1 text-[11px] text-neutral-600">{entry.note}</p>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export default function Home() {
  const { brand } = useBrand();
  const accentColor = brand.primaryColor || '#12b886';
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedItem, setSelectedItem] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCategoryMenu, setShowCategoryMenu] = useState(false);
  const { cartItems, itemCount, total } = useContext(CartContext);
  const {
    session: storedTableSession,
    participant: tableParticipant,
    restore: restoreTableSession,
    syncDraft: syncTableDraft,
  } = useContext(TableSessionContext);
  const [showSharedTable, setShowSharedTable] = useState(false);
  const [showJoinTable, setShowJoinTable] = useState(false);
  const [sharedTableInitialView, setSharedTableInitialView] = useState('main');
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const {
    merchantId: stateMerchantId,
    table_number: stateTableNumber,
    revenue_center: stateRevenueCenter,
    table_session_id: requestedSessionId = '',
  } = location.state || {};
  const merchantId = stateMerchantId || localStorage.getItem('merchant_id') || '';
  const table_number = stateTableNumber || localStorage.getItem('table_number') || '';
  const revenueCenter = stateRevenueCenter || localStorage.getItem('revenue_center') || 'default';
  const orderType = table_number && table_number !== '0' ? 'dine_in' : 'takeaway';
  const activeTableSession = storedTableSession
    && storedTableSession.restaurant_id === merchantId
    && String(storedTableSession.restaurant_tables?.table_number || '') === String(table_number)
    ? storedTableSession
    : null;
  const hasTableContext = orderType === 'dine_in' && Boolean(table_number) && table_number !== '0';

  useEffect(() => {
    if (stateTableNumber) localStorage.setItem('table_number', stateTableNumber);
    if (stateRevenueCenter) localStorage.setItem('revenue_center', stateRevenueCenter);
  }, [stateRevenueCenter, stateTableNumber]);

  const { data: merchant, isLoading: isLoadingMerchant } = GetMerchantProfile(merchantId);
  const { data: merchantSettings, isLoading: isLoadingSettings } = useMerchantSettings(merchantId);
  const { data: menuCategories, isLoading: isLoadingMenu } = GetMenuItemsByMerchant(merchantId);
  const { data: activeCampaigns = [] } = GetActiveCampaigns(merchantId);
  const { data: allRewards = [] } = GetRewards(merchantId);
  const canShareTable = merchantSettings?.group_ordering_enabled === true && hasTableContext;

  useEffect(() => {
    if (canShareTable) return;
    setShowJoinTable(false);
    setShowSharedTable(false);
  }, [canShareTable]);

  useEffect(() => {
    const refCode = searchParams.get('ref');
    const shoutoutToken = searchParams.get('shoutout');

    if (refCode) {
      localStorage.setItem('referral_code', refCode);
    }
    if (shoutoutToken) {
      localStorage.setItem('shoutout_id', shoutoutToken);
    }
  }, [searchParams]);

  useEffect(() => {
    if (!merchantId || !hasTableContext) return undefined;
    let cancelled = false;
    restoreTableSession({ restaurantId: merchantId, tableRef: table_number, sessionId: requestedSessionId })
      .then((restored) => {
        if (!cancelled && canShareTable && requestedSessionId && !restored) setShowJoinTable(true);
      });
    return () => { cancelled = true; };
  }, [canShareTable, hasTableContext, merchantId, requestedSessionId, restoreTableSession, table_number]);

  useEffect(() => {
    if (!activeTableSession?.id || !canShareTable) return undefined;
    const timer = window.setTimeout(() => syncTableDraft(cartItems).catch(() => {}), 500);
    return () => window.clearTimeout(timer);
  }, [activeTableSession?.id, canShareTable, cartItems, syncTableDraft]);

  const customerPhone = localStorage.getItem('customer_phone') || '';
  const { data: customerProfile } = useCustomerLookup(merchantId, customerPhone || null);
  const customerPoints = customerProfile?.total_points || 0;
  const qualifyingRewards = allRewards.filter((reward) => reward.is_active && customerPoints >= reward.points_required);

  const categories = useMemo(() => (
    Array.isArray(menuCategories) ? menuCategories : []
  ), [menuCategories]);
  const merchantInfo = merchant || FALLBACK_MERCHANT;
  const restaurantName = merchantInfo.name || merchantInfo.restaurant_name || FALLBACK_MERCHANT.name;
  const restaurantLogo = brand.logoUrl || merchantInfo.logo_url || merchantInfo.logo || '';
  const tableLabel = orderType === 'takeaway' ? 'Takeaway' : (table_number ? `Table ${table_number}` : 'Dine in');

  const allItems = useMemo(() => (
    categories.flatMap((category) => {
      const categoryName = category.name || category.category_name || 'Menu';
      return (category.menu_items || category.items || []).map((item) => ({
        ...item,
        id: item.item_id || item.id,
        name: item.item_name || item.name,
        description: normalizeText(item.description),
        price: Number(item.price || 0),
        image: item.image_url || item.image || '',
        item_name: item.item_name || item.name,
        item_public_id: item.item_public_id || item.item_id || item.id || `${categoryName}-${item.item_name || item.name}`,
        category: categoryName,
      }));
    })
  ), [categories]);

  const visibleItems = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return allItems.filter((item) => {
      const matchesQuery = !query ||
        (item.name || '').toLowerCase().includes(query) ||
        (item.description || '').toLowerCase().includes(query);
      const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
      return matchesQuery && matchesCategory;
    });
  }, [allItems, searchQuery, selectedCategory]);

  const visibleSections = useMemo(() => {
    const orderedNames = categories.map((category) => category.name || category.category_name).filter(Boolean);
    const names = selectedCategory === 'all' ? orderedNames : [selectedCategory];
    return names.map((name) => ({
      name,
      items: visibleItems.filter((item) => item.category === name),
    })).filter((section) => section.items.length);
  }, [categories, selectedCategory, visibleItems]);

  const topSellers = useMemo(() => {
    const withImages = allItems.filter((item) => item.image);
    return (withImages.length ? withImages : allItems).slice(0, 8);
  }, [allItems]);

  if (!merchantId || isLoadingMerchant || isLoadingSettings || isLoadingMenu) {
    return (
      <div className="min-h-screen bg-white px-6 pt-14">
        <div className="mx-auto max-w-md animate-pulse space-y-6">
          <div className="h-8 w-32 rounded bg-neutral-200" />
          <div className="h-12 rounded-xl bg-neutral-100" />
          <div className="h-36 rounded-xl bg-neutral-100" />
          {[1, 2, 3].map((item) => (
            <div key={item} className="flex gap-3">
              <div className="h-20 w-20 rounded-lg bg-neutral-100" />
              <div className="flex-1 space-y-2">
                <div className="h-4 w-3/4 rounded bg-neutral-100" />
                <div className="h-3 w-full rounded bg-neutral-100" />
                <div className="h-4 w-16 rounded bg-neutral-100" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white pb-28 text-neutral-950">
      <div className="mx-auto max-w-md px-4 pt-5 sm:max-w-2xl sm:px-5">
        <header>
          <div className="flex items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              {restaurantLogo ? (
                <span className="relative flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-neutral-200 bg-neutral-100 text-[15px] font-bold uppercase text-neutral-600">
                  {restaurantName.charAt(0)}
                  <img
                    src={restaurantLogo}
                    alt={restaurantName}
                    onError={(event) => { event.currentTarget.style.display = 'none'; }}
                    className="absolute inset-0 h-full w-full bg-white object-cover"
                  />
                </span>
              ) : (
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-neutral-100">
                  <HandPlatter className="h-5 w-5 text-neutral-500" />
                </span>
              )}
              <div className="min-w-0">
                <h1 className="truncate text-[19px] font-bold leading-6 text-neutral-950">{restaurantName}</h1>
                <p className="mt-0.5 text-[12px] font-medium text-neutral-500">Browse the menu</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => navigate('/my-account')}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-neutral-200 bg-white"
              aria-label="Account"
            >
              <UserIcon className="h-4 w-4 text-neutral-700" />
            </button>
          </div>

          <div className="mt-4 grid grid-cols-[minmax(0,auto)_minmax(132px,1fr)_auto] items-center gap-2">
            <span className="min-w-0 truncate rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2 text-center text-[12px] font-bold text-neutral-800">
              {tableLabel}
            </span>
            <div className="grid grid-cols-2 rounded-lg bg-neutral-100 p-1">
              <button
                type="button"
                onClick={() => {
                  setSelectedCategory('all');
                  setSearchQuery('');
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className="h-8 rounded-md bg-white text-[12px] font-bold text-neutral-950 shadow-sm"
              >
                Menu
              </button>
              <button
                type="button"
                onClick={() => navigate('/cart', { state: { merchantId, table_number, revenue_center: revenueCenter, merchantName: restaurantName, orderType, table_session_id: activeTableSession?.id || requestedSessionId } })}
                className="h-8 rounded-md text-[12px] font-semibold text-neutral-600"
              >
                Order{itemCount > 0 && <span className="ml-1 font-bold" style={{ color: accentColor }}>{itemCount}</span>}
              </button>
            </div>
            <button
              type="button"
              onClick={() => navigate('/my-account')}
              className="flex h-10 min-w-10 items-center justify-center gap-1.5 rounded-full border border-neutral-200 bg-white px-2.5 text-[11px] font-bold text-neutral-800"
              aria-label={`${Number(customerPoints || 0).toLocaleString()} points. View account`}
            >
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700">
                <Coins className="h-3.5 w-3.5" />
              </span>
              <span>{Number(customerPoints || 0).toLocaleString()}</span>
            </button>
          </div>
        </header>

        {canShareTable && (
          <button
            type="button"
            onClick={() => {
              if (activeTableSession) {
                setSharedTableInitialView('main');
                setShowSharedTable(true);
              } else {
                setShowJoinTable(true);
              }
            }}
            className="mt-4 grid w-full grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-3 rounded-xl bg-[#f3f2ee] px-3 py-3 text-left transition-colors hover:bg-[#ebeae5] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400 active:bg-[#e4e3dd]"
            aria-label={activeTableSession ? 'Open group order' : 'Start group order'}
          >
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg" style={{ backgroundColor: `${accentColor}14` }}>
              <Users className="h-4 w-4" style={{ color: accentColor }} />
            </span>
            <div className="min-w-0 self-center">
              <p className="whitespace-nowrap text-[14px] font-bold leading-5 text-neutral-950">Order together</p>
              <p className="mt-0.5 text-[11px] leading-4 text-neutral-700 sm:text-[12px]">
                {activeTableSession
                  ? tableParticipant?.is_host
                    ? `You started this group - Table ${table_number}`
                    : `Ordering with ${activeTableSession.participants?.find((person) => person.is_host)?.display_name || 'your group'} - Table ${table_number}`
                  : 'Everyone chooses from their own phone'}
              </p>
            </div>
            <span className="flex shrink-0 items-center gap-2 text-[12px] font-semibold text-neutral-700">
              {activeTableSession && (
                <span className="whitespace-nowrap">
                  {tableParticipant?.is_host
                    ? Math.max(0, Number(activeTableSession.summary?.participantCount || activeTableSession.participants?.length || 1) - 1) === 0
                      ? 'No guests yet'
                      : `${Math.max(0, Number(activeTableSession.summary?.participantCount || activeTableSession.participants?.length || 1) - 1)} guest${Math.max(0, Number(activeTableSession.summary?.participantCount || activeTableSession.participants?.length || 1) - 1) === 1 ? '' : 's'}`
                    : `${activeTableSession.summary?.participantCount || activeTableSession.participants?.length || 1} joined`}
                </span>
              )}
              <ChevronRight className="h-4 w-4 text-neutral-500" />
            </span>
          </button>
        )}

        <div className="mt-5 flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowCategoryMenu(true)}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-white"
            style={{ backgroundColor: accentColor }}
            aria-label="Browse menu categories"
          >
            <MenuIcon className="h-4 w-4" />
          </button>
          <div className="flex h-11 min-w-0 flex-1 items-center gap-2.5 rounded-xl border border-neutral-300 bg-white px-3">
            <Search className="h-4 w-4 shrink-0 text-neutral-400" />
            <input
              type="text"
              placeholder="Search the menu"
              value={searchQuery}
              onFocus={() => setSelectedCategory('all')}
              onChange={(event) => {
                setSearchQuery(event.target.value);
                if (event.target.value) setSelectedCategory('all');
              }}
              className="h-full min-w-0 flex-1 bg-transparent text-[15px] text-neutral-950 outline-none placeholder:text-neutral-400"
            />
            {searchQuery && (
              <button type="button" onClick={() => setSearchQuery('')} aria-label="Clear search">
                <X className="h-4 w-4 text-neutral-400" />
              </button>
            )}
          </div>
        </div>

        {!searchQuery.trim() && <OfferStrip activeCampaigns={activeCampaigns} qualifyingRewards={qualifyingRewards} accentColor={accentColor} />}

        {!searchQuery.trim() && topSellers.length > 0 && (
          <section className="mt-6">
            <h2 className="text-[17px] font-bold leading-none">Menu highlights</h2>
            <div className="mt-3 flex snap-x gap-3 overflow-x-auto pb-1 scrollbar-none">
              {topSellers.map((item) => (
                <TopSellerCard key={`top-${itemKey(item)}`} item={item} onSelect={setSelectedItem} />
              ))}
            </div>
          </section>
        )}

        <div id="menu-categories" className="sticky top-0 z-30 -mx-4 mt-4 scroll-mt-2 border-b border-neutral-200 bg-white/95 px-4 py-3 backdrop-blur sm:-mx-5 sm:px-5">
          <div className="flex gap-2 overflow-x-auto pb-0.5 scrollbar-none">
            <button
              type="button"
              onClick={() => setSelectedCategory('all')}
              className="h-8 shrink-0 rounded-full border px-3.5 text-[13px] font-medium"
              style={selectedCategory === 'all' ? { borderColor: accentColor, backgroundColor: `${accentColor}12`, color: '#111' } : { borderColor: '#d4d4d4', color: '#404040' }}
            >
              All
            </button>
            {categories.map((category) => {
              const categoryName = category.name || category.category_name;
              if (!categoryName) return null;
              return (
                <button
                  key={categoryName}
                  type="button"
                  onClick={() => setSelectedCategory(categoryName)}
                  className="h-8 shrink-0 rounded-full border px-3.5 text-[13px] font-medium"
                  style={selectedCategory === categoryName ? { borderColor: accentColor, backgroundColor: `${accentColor}12`, color: '#111' } : { borderColor: '#d4d4d4', color: '#404040' }}
                >
                  {categoryName}
                </button>
              );
            })}
          </div>
        </div>

        <main>
          {visibleItems.length === 0 ? (
            <div className="py-20 text-center">
              <HandPlatter className="mx-auto h-7 w-7 text-neutral-300" />
              <p className="mt-3 text-[14px] font-semibold text-neutral-700">
                {searchQuery ? 'No matching dishes' : 'No menu items available'}
              </p>
              <p className="mt-1 text-[13px] text-neutral-500">Try another search or check again later.</p>
            </div>
          ) : (
            visibleSections.map((section) => (
              <section key={section.name} className="pt-5">
                <div className="mb-1 flex items-end justify-between">
                  <h2 className="text-[19px] font-bold tracking-tight text-neutral-950">{section.name}</h2>
                  <span className="text-[12px] text-neutral-400">{section.items.length}</span>
                </div>
                <div className="space-y-2">
                  {section.items.map((item) => (
                    <MenuItemRow
                      key={itemKey(item)}
                      item={item}
                      accentColor={accentColor}
                      onSelect={setSelectedItem}
                    />
                  ))}
                </div>
              </section>
            ))
          )}
        </main>
      </div>

      <div className="fixed inset-x-0 bottom-0 z-40 bg-white/95 px-5 pb-4 pt-3 backdrop-blur">
        <div className="mx-auto max-w-md sm:max-w-2xl">
          {itemCount > 0 ? (
            <button
              type="button"
              onClick={() => navigate('/cart', { state: { merchantId, table_number, revenue_center: revenueCenter, merchantName: restaurantName, orderType, table_session_id: activeTableSession?.id || requestedSessionId } })}
              className="flex h-14 w-full items-center justify-between rounded-xl px-5 text-[15px] font-bold text-white active:scale-[0.99]"
              style={{ backgroundColor: accentColor }}
            >
              <span>View order · {itemCount} item{itemCount === 1 ? '' : 's'}</span>
              <span>{formatCurrency(Number(total || 0))}</span>
            </button>
          ) : (
            <div className="grid grid-cols-3 rounded-2xl border border-neutral-200 bg-white px-2 py-2 shadow-lg shadow-neutral-900/[0.06]">
              <button type="button" onClick={() => navigate('/my-account')} className="flex flex-col items-center gap-1 py-1 text-[11px] font-semibold text-neutral-500">
                <UserIcon className="h-5 w-5" />
                Account
              </button>
              <button
                type="button"
                onClick={() => {
                  setSelectedCategory('all');
                  setSearchQuery('');
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className="flex flex-col items-center gap-1 py-1 text-[11px] font-semibold"
                style={{ color: accentColor }}
              >
                <HandPlatter className="h-5 w-5" />
                Menu
              </button>
              <button type="button" onClick={() => navigate('/cart', { state: { merchantId, table_number, revenue_center: revenueCenter, merchantName: restaurantName, orderType, table_session_id: activeTableSession?.id || requestedSessionId } })} className="flex flex-col items-center gap-1 py-1 text-[11px] font-semibold text-neutral-500">
                <ShoppingBag className="h-5 w-5" />
                Cart
              </button>
            </div>
          )}
        </div>
      </div>

      {selectedItem && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 px-0 pt-8 sm:items-center sm:px-4">
          <div className="h-[90vh] w-full overflow-hidden rounded-t-[26px] bg-[#f7f7f5] shadow-2xl sm:max-w-md sm:rounded-[26px]">
            <ItemCustomization item={selectedItem} onClose={() => setSelectedItem(null)} />
          </div>
        </div>
      )}

      {showCategoryMenu && (
        <div className="fixed inset-0 z-[55] flex items-end justify-center bg-black/45 sm:items-center sm:p-4" onClick={() => setShowCategoryMenu(false)}>
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="menu-category-title"
            className="flex max-h-[78dvh] w-full flex-col overflow-hidden rounded-t-2xl bg-white sm:max-w-md sm:rounded-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex shrink-0 items-center justify-between border-b border-neutral-200 px-5 py-4">
              <div>
                <h2 id="menu-category-title" className="text-[18px] font-bold text-neutral-950">Browse menu</h2>
                <p className="mt-0.5 text-[12px] text-neutral-500">Choose a category</p>
              </div>
              <button type="button" onClick={() => setShowCategoryMenu(false)} className="flex h-9 w-9 items-center justify-center rounded-full bg-neutral-100" aria-label="Close categories">
                <X className="h-4 w-4 text-neutral-700" />
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-2 [scrollbar-width:thin]">
              {[
                { name: 'all', label: 'All menu items', count: allItems.length },
                ...categories.map((category) => ({
                  name: category.name || category.category_name,
                  label: category.name || category.category_name,
                  count: (category.menu_items || category.items || []).length,
                })).filter((category) => category.name),
              ].map((category) => {
                const active = selectedCategory === category.name;
                return (
                  <button
                    key={category.name}
                    type="button"
                    onClick={() => {
                      setSelectedCategory(category.name);
                      setSearchQuery('');
                      setShowCategoryMenu(false);
                      window.setTimeout(() => document.getElementById('menu-categories')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
                    }}
                    className="flex w-full items-center gap-3 border-b border-neutral-100 py-4 text-left last:border-b-0"
                  >
                    <span className="min-w-0 flex-1 truncate text-[15px] font-semibold text-neutral-900">{category.label}</span>
                    <span className="text-[12px] text-neutral-500">{category.count}</span>
                    <span className="flex h-5 w-5 items-center justify-center">
                      {active && <Check className="h-4 w-4" style={{ color: accentColor }} />}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <JoinTableSheet
        open={showJoinTable && canShareTable && !activeTableSession}
        onClose={() => setShowJoinTable(false)}
        onStarted={() => {
          setSharedTableInitialView('invite');
          setShowSharedTable(true);
        }}
        restaurantId={merchantId}
        tableRef={table_number}
        requestedSessionId={requestedSessionId}
        accentColor={accentColor}
      />

      <SharedTablePanel
        open={showSharedTable}
        onClose={() => setShowSharedTable(false)}
        accentColor={accentColor}
        tableRef={table_number}
        initialView={sharedTableInitialView}
        joinUrlOverride={`/menu/${encodeURIComponent(merchantId)}/${encodeURIComponent(table_number)}/${encodeURIComponent(revenueCenter)}`}
      />
    </div>
  );
}
